// Package imports:
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleUp, faAngleDown } from "@fortawesome/pro-regular-svg-icons";
import { faCircleXmark } from "@fortawesome/pro-solid-svg-icons";

type SelectSize = "sm" | "lg" | "xs";

export type OptionValue = string | number;

export interface IOptionProps {
  id?: string;
  label: string | JSX.Element;
  value: OptionValue;
  disabledOption?: boolean;
}

interface IOwnProps {
  label?: string;
  options?: IOptionProps[];
  size?: SelectSize;
  className?: string | React.HTMLAttributes<HTMLElement>;
  defaultValue?: OptionValue | OptionValue[] | null;
  onChange?(s?: OptionValue | OptionValue[] | null): void;
  disabled?: boolean;
  usePortal?: boolean; // New prop to enable portal rendering
  clearInput?: boolean;
  children?: React.ReactNode;
}

export type DropSelectProps = IOwnProps;

const isValidValue = (value: unknown): value is OptionValue => {
  return value != null && (typeof value === 'string' || typeof value === 'number');
};

const DropSelect: React.FC<DropSelectProps> = ({
  label,
  size = "sm",
  options = [],
  className = "",
  defaultValue = "",
  onChange = () => { },
  disabled = false,
  usePortal = false, // Default to false to maintain backward compatibility
  clearInput = false,
  children,
}) => {
  const dropdown = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null); // Reference to the trigger button for portal positioning
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [highlightedIndex, setHighlightedIndex] = useState(-1); // Track which item is highlighted on hover
  const isMounted = useRef(false);

  const toggleOpen = useCallback(() => setIsOpen(prev => !prev), []);

  const sanitizedOptions = useMemo(() => {
    return options.map((opt) =>
      isValidValue(opt.value) ? opt : { ...opt, disabledOption: true }
    );
  }, [options]);

  const hasValue = useMemo(() => {
    return isValidValue(selectedValue) && String(selectedValue).length >= 1;
  }, [selectedValue]);

  const selectedOption = useMemo(() => {
    if (!isValidValue(selectedValue)) return undefined;
    const sv = String(selectedValue);
    return sanitizedOptions.find((opt) => isValidValue(opt.value) && String(opt.value) === sv);
  }, [selectedValue, sanitizedOptions]);

  // Calculate dropdown position for portal rendering
  const updateDropdownPosition = () => {
    if (dropdown.current && triggerRef.current && usePortal) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const containerRect = dropdown.current.getBoundingClientRect();

      // Position the dropdown directly below the trigger button
      const newPosition = {
        top: triggerRect.bottom,
        left: triggerRect.left,
        width: containerRect.width,
      };

      setDropdownPosition(newPosition);
    }
  };

  const handleOutsideClick = (event: any) => {
    if (dropdown.current) {
      if (!dropdown.current.contains(event.target)) {
        // When using portal, also check if the click was inside the portal overlay
        if (usePortal) {
          const portalOverlay = document.querySelector(".drop-select__overlay--portal");
          if (portalOverlay && portalOverlay.contains(event.target)) {
            return; // Don't close if clicking inside portal overlay
          }
        }
        setIsOpen(false);
      }
    }
  };

  // Setup event listeners for outside clicks and portal positioning
  useEffect(() => {
    window.addEventListener("click", handleOutsideClick);

    // Add scroll and resize listeners when using portal to maintain correct positioning
    if (usePortal) {
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
    }

    return () => {
      window.removeEventListener("click", handleOutsideClick);
      if (usePortal) {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usePortal]); // Include usePortal in dependencies to re-run when it changes

  // Handle side effects when selection changes (close dropdown, notify parent)
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(selectedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  // Update portal position when dropdown opens
  useEffect(() => {
    if (isOpen && usePortal) {
      // Use multiple timeouts to ensure positioning works correctly across different scenarios
      const positionAttempts = [0, 10, 50, 100];

      positionAttempts.forEach((delay) => {
        setTimeout(() => {
          updateDropdownPosition();
        }, delay);
      });
    }
  }, [isOpen, usePortal]);

  const handleTriggerClick = useCallback(() => {
    if (!disabled) {
      toggleOpen();
      setHighlightedIndex(-1);
      if (!isOpen && usePortal) {
        requestAnimationFrame(() => {
          updateDropdownPosition();
        });
      }
    }
  }, [disabled, isOpen, usePortal]);

  const sortedOptions = useMemo(() => {
    return [...sanitizedOptions].sort((a, b) => {
      if (a.disabledOption && !b.disabledOption) return 1;
      if (!a.disabledOption && b.disabledOption) return -1;
      return 0;
    });
  }, [sanitizedOptions]);

  const renderOverlayContent = () => (
    <div className="drop-select__inner">
      {children ? (
        <div>{children}</div>
      ) : (
        sortedOptions.length > 0 &&
        sortedOptions.map((option, i) => (
          <button
            key={isValidValue(option.value) ? option.value : `invalid-${option.id ?? option.label}`}
            type="button"
            disabled={option.disabledOption}
            className={cx("drop-select__item", {
              "is-selected": isValidValue(selectedValue) && isValidValue(option.value) && String(option.value) === String(selectedValue),
              "is-highlighted": i === highlightedIndex,
              "is-disabled": option.disabledOption,
            })}
            onClick={() => setSelectedValue(option.value)}
            onMouseEnter={() => setHighlightedIndex(i)}
            onMouseLeave={() => setHighlightedIndex(-1)}
          >
            {option.label}
          </button>
        ))
      )}
    </div>
  );

  return (
    <>
      <div
        className={cx("KCL_drop-select", className.toString(), size, {
          "is-open": isOpen,
          "has-value": hasValue,
          disabled: disabled,
          "drop-select--portal": usePortal, // Add portal class for styling
        })}
        ref={dropdown}
      >
        {label && (
          <span className="drop-select__label">
            <span>{label}</span>
          </span>
        )}

        <button
          onClick={handleTriggerClick}
          className="drop-select__trigger btn"
          type="button"
          ref={triggerRef} // Reference for portal positioning
        >
          <span>{selectedOption ? selectedOption.label : ""}</span>
          <div>
            {clearInput && hasValue && !disabled && (
              <i onClick={() => {
                setSelectedValue("");
              }}
                className="drop-select__clear-icon input-icon"
              ><FontAwesomeIcon icon={faCircleXmark} /></i>
            )}
            <i className="input-icon">
              <FontAwesomeIcon icon={isOpen ? faAngleUp : faAngleDown} />
            </i>
          </div>
        </button>

        {!usePortal && isOpen && (
          <div className="drop-select__overlay">
            {renderOverlayContent()}
          </div>
        )}
      </div>

      {usePortal &&
        isOpen &&
        createPortal(
          <div
            className={cx("drop-select__overlay drop-select__overlay--portal", size)}
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              zIndex: 9999,
            }}
          >
            {renderOverlayContent()}
          </div>,
          document.body
        )}
    </>
  );
};

export default DropSelect;
